import React from "react";
import styled from "@emotion/styled";

import { FeatureType } from "components/paywall/paywallUtils";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { EventNames, useTracking } from "lib/tracking";
import { DataTypeNameWithBuyersAndSuppliers } from "lib/types/models";
import UIcon from "../../lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault } from "../../lib/themes/colors";
import { Flex, Text } from "../../styles/utility-components";
import Button from "../ui/button/Button";
import PaidTag from "./PaidTag";
import {
  AllEntities,
  entityToDataType,
  getEntityEmptyStateDescription,
  getEntityLabel,
} from "./utils";

import AddEntitySvg from "../../../assets/images/add_entity.svg";

const dataTypeToPaywall = (dataType: DataTypeNameWithBuyersAndSuppliers): FeatureType | null => {
  // Not including BUYERS since the core feature is free which includes adding buyers to a lead or bid
  if (["DOCUMENTS", "FRAMEWORKS", "SUPPLIERS", "CONTACTS"].includes(dataType)) {
    return dataType as FeatureType;
  }
  return null;
};

type Props = {
  entity: AllEntities;
  url?: string;
  isEmptyState?: boolean;
  showAddEntity?: boolean;
  variant?: "card" | "inline";
};

export default function FindEntity({
  entity,
  url,
  isEmptyState,
  showAddEntity = true,
  variant = "card",
}: Props) {
  const { logEvent } = useTracking();
  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();

  const dataType = entityToDataType(entity);
  const hasDataType = subscription.hasDataTypes(dataType);
  const paywall = dataTypeToPaywall(dataType);
  const hasPaywall = paywall && !hasDataType;

  const onClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (hasPaywall) {
      e.preventDefault();
      openPaywallModal(paywall);
    } else {
      logEvent(EventNames.saveDataClicked, {
        "Data type": entity.toUpperCase(),
      });
    }
  };

  if (variant === "inline") {
    const label = `Find ${getEntityLabel(entity, false)}`;
    // The paywalled variant has nowhere to link to — clicking opens the upgrade modal instead.
    if (hasPaywall) {
      return (
        <Button
          buttonType="primary"
          buttonStyle="inverse"
          icon="plus"
          iconProps={{ color: "sysTextSecondary" }}
          onClick={onClick}
        >
          {label}
        </Button>
      );
    }
    if (!url) return null;
    return (
      <Button
        as="a"
        href={url}
        buttonType="primary"
        buttonStyle="inverse"
        icon="plus"
        iconProps={{ color: "sysTextSecondary" }}
        onClick={onClick}
      >
        {label}
      </Button>
    );
  }

  const shouldShowAddEntity = showAddEntity && !isEmptyState;

  if (shouldShowAddEntity) {
    if (hasPaywall) {
      return (
        <Button as="button" buttonType="secondary" onClick={onClick} icon="plus" fullWidth>
          {getEntityLabel(entity)}
        </Button>
      );
    }
    if (!url) return null;
    return (
      <Button as="a" href={url} buttonType="secondary" onClick={onClick} icon="plus" fullWidth>
        {getEntityLabel(entity)}
      </Button>
    );
  }

  if (!isEmptyState) {
    return null;
  }

  return (
    <StyledLink
      href={hasPaywall ? undefined : url}
      onClick={onClick}
      applyLinkStyle={!isEmptyState}
    >
      <Card>
        <Flex width="100%" justifyContent="space-between" alignItems="flex-start">
          <Flex gap={12} alignItems="center">
            <img src={AddEntitySvg} alt="Find entity" height={82} />
            <Description>
              <Text bold>Find {getEntityLabel(entity, false)}</Text>
              <Text description>{getEntityEmptyStateDescription(entity)}</Text>
            </Description>
          </Flex>
          {hasPaywall ? (
            <PaidTag />
          ) : (
            <IconWrapper>
              <UIcon icon="plus" color={sysPrimaryDefault} />
            </IconWrapper>
          )}
        </Flex>
      </Card>
    </StyledLink>
  );
}

const Card = styled(Flex)(({ theme }) => ({
  display: "flex",
  width: "100%",
  padding: 15,
  borderRadius: 8,
  backgroundImage: `repeating-linear-gradient(90deg, ${theme.colors.sysBorderDecorative}, ${theme.colors.sysBorderDecorative} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysBorderDecorative}, ${theme.colors.sysBorderDecorative} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(90deg, ${theme.colors.sysBorderDecorative}, ${theme.colors.sysBorderDecorative} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysBorderDecorative}, ${theme.colors.sysBorderDecorative} 12px, transparent 12px, transparent 28px)`,
  backgroundPosition: "left top, right top, left bottom, left top",
  backgroundRepeat: "repeat-x, repeat-y, repeat-x, repeat-y",
  backgroundSize: "100% 1px, 1px 100%, 100% 1px, 1px 100%",
  transition: "all 0.2s ease-in-out",
}));

const StyledLink = styled.a<{ applyLinkStyle?: boolean }>(({ theme, applyLinkStyle }) => ({
  all: "unset",
  cursor: "pointer",
  display: "block",
  width: "100%",
  color: applyLinkStyle ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault,
  "&:hover": {
    color: applyLinkStyle ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault,
    [`${Card}`]: {
      backgroundColor: theme.colors.sysPrimarySubtle,
      backgroundImage: `repeating-linear-gradient(90deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(90deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px)`,
    },
  },
}));

const Description = styled(Flex)({
  flexDirection: "column",
  gap: 4,
});

const IconWrapper = styled(Flex)({
  padding: "5px 8px",
});
